import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  return {
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
    visualViewport: window.visualViewport
      ? {
          width: window.visualViewport.width,
          height: window.visualViewport.height,
          scale: window.visualViewport.scale,
          offsetLeft: window.visualViewport.offsetLeft,
          offsetTop: window.visualViewport.offsetTop,
        }
      : null,
  };
}

function validateViewport(rawData: RawData): ValidationResult {
  const keys = [
    'outerWidth',
    'outerHeight',
    'innerWidth',
    'innerHeight',
    'clientWidth',
    'clientHeight',
  ];
  const reasons = keys
    .filter(
      (key) =>
        !Number.isFinite(Number(rawData[key])) || Number(rawData[key]) <= 0,
    )
    .map((key) => `${key} must be positive`);
  if (
    Number(rawData.clientWidth) > Number(rawData.innerWidth) ||
    Number(rawData.clientHeight) > Number(rawData.innerHeight)
  ) {
    reasons.push('Document client viewport exceeds the window inner viewport');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${String(rawData.innerWidth)}×${String(rawData.innerHeight)} viewport`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateViewport(rawData);
}

export const viewportTest: TestDefinition = {
  id: 'controlledViewport',
  title: 'Controlled viewport',
  category: 'shared',
  format,
  run,
  validate,
};
